import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowUpFromLine, Lock, Save, Trash2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Product } from "../types";
import { trpc } from "@/lib/trpc";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import { toast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { useRole } from "@/hooks/use-role";
import { PushConfirmDialog } from "@/features/stock-sync/components/PushConfirmDialog";
import { AdditiveBreakdownIcon } from "@/features/stock-sync/components/AdditiveBreakdown";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useLocation } from "@/contexts/LocationContext";
import { resolveInventoryPushSettings } from "@/features/organization/components/settingsShared";
import type { OrganizationSettings as SharedOrganizationSettings } from "@threadheads/shared";
import { joinParts } from "../utils/joinParts";
import { resolveInventoryPushContext } from "../utils/inventoryPushContext";
import { aliasStockWriteMessage, isOwnRow } from "../utils/supplyAliasCopy";
import RecordScrapModal from "./RecordScrapModal";
import { SupplyBadge } from "@/features/supplier-catalog/components/SupplyBadge";
import { SupplierOutChip } from "./SupplierOutChip";

interface InventoryProductDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  // BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved Product (not sku
  // string). Aligns with useInventory's IDs-not-strings rule.
  // BLU-681: 4th arg carries the row's owner org so a 3PL owner's edit on a
  // client-owned SKU is intent-explicit (absent also works — the backend
  // adopts the existing row's org — this is belt on top).
  onSetStock?: (
    product: Product,
    quantity: number,
    locationId?: string,
    targetOrganizationId?: string,
  ) => Promise<boolean> | boolean;
}

export default function InventoryProductDetailModal({
  open,
  onOpenChange,
  product,
  onSetStock,
}: InventoryProductDetailModalProps) {
  const { t } = useTranslation('inventory');
  const skuId = product?.id ?? "";
  const [pendingLocationId, setPendingLocationId] = useState<string | null>(null);
  const [stockInputs, setStockInputs] = useState<Record<string, string>>({});
  const [thresholdInputs, setThresholdInputs] = useState<Record<string, string>>({});
  // Stock Hold-Back layer 1. Kept as strings so BLANK ('' = clear the override,
  // fall back to the type rule) stays distinguishable from '0' ('hold nothing
  // back here'). The threshold field above deliberately does NOT make this
  // distinction, which is why this is separate state rather than a reuse.
  const [holdBackInputs, setHoldBackInputs] = useState<Record<string, string>>({});
  const [invalidFields, setInvalidFields] = useState<Record<string, 'stock' | 'threshold' | 'holdBack' | null>>({});
  const { isAdmin, can } = useRole();
  const canRecordScrap = can("inventory.write");
  // BLU-681: owner-only stock policy. `is_shared` marks locations this org
  // accesses as a 3PL CLIENT (junction) — stock there is managed by the
  // provider, so the corresponding breakdown rows render read-only (matching
  // the Inventory toolbar's banner; the backend enforces the same policy).
  const { locations: accessibleLocations } = useLocation();
  // BLU-711 A1: ...unless the owner has opted this org in to writing its own
  // stock at that location, in which case the breakdown rows stay editable.
  // Must match the Inventory toolbar gate and the backend
  // assertCallerManagesLocationStock — three surfaces, one predicate.
  const managedByProviderLocationIds = useMemo(
    () =>
      new Set(
        (accessibleLocations ?? [])
          .filter((loc) => loc.is_shared && !loc.client_stock_writes_enabled)
          .map((loc) => loc.id),
      ),
    [accessibleLocations],
  );
  const [scrapLocation, setScrapLocation] = useState<{
    locationId: string;
    locationName: string;
    onHand: number;
  } | null>(null);
  const { organization } = useOrganization();
  const productPushContext = resolveInventoryPushContext({
    activeOrganizationId: organization?.id,
    viewedOrganizationId: product?.ownerOrganizationId,
  });
  const pushSettings = useMemo(
    () => resolveInventoryPushSettings(organization?.settings as SharedOrganizationSettings | null),
    [organization?.settings],
  );
  // BLU-354d: hybrid data comes from the breakdown query itself — no
  // separate tRPC query needed. Each location row carries `hybridMode` and
  // the related-family counts for the tooltip.
  const productionType = product?.productionType ?? null;
  const isHybridEligibleProduct = pushSettings.enabled && (productionType === 'PAP' || productionType === 'POD');
  const [pushingLocationId, setPushingLocationId] = useState<string | null>(null);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const utils = trpc.useUtils();

  const updateThresholdMutation = trpc.inventory.updateNotifyThreshold.useMutation({
    onError: (error) => {
      toast({
        title: "Failed to update threshold",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Stock Hold-Back layer 1 write. The new value only reaches Shopify on the
  // NEXT push cycle: a hold-back change moves no stock, so change detection
  // would otherwise never notice it (the backend stamps repush markers).
  const setHoldBackMutation = trpc.inventory.setPushSafetyBuffer.useMutation({
    onError: (err) => {
      toast({
        title: 'Hold-back not saved',
        description: err instanceof Error ? err.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const pushSku = trpc.stockSync.pushSingleSku.useMutation({
    onSuccess: (result) => {
      const prefix = result.dryRun ? "[DRY RUN] " : "";
      // Build detail description from tracking/activation counts
      const details: string[] = [];
      if (result.trackingEnabled > 0) details.push(`${result.trackingEnabled} tracking enabled`);
      if (result.activated > 0) details.push(`${result.activated} activated`);
      const detailStr = details.length > 0 ? details.join(', ') : undefined;

      if (result.success && result.errors === 0) {
        if (result.updated === 0) {
          sonnerToast.warning(`${prefix}${result.message}`, { duration: 6000 });
        } else {
          sonnerToast.success(`${prefix}${result.message}`, { description: detailStr });
        }
      } else if (result.success && result.errors > 0) {
        sonnerToast.warning(`${prefix}${result.message}`, { description: detailStr, duration: 6000 });
      } else if (result.noop) {
        // BLU-550 Phase 0: a no-op push (nothing eligible / no Shopify variants
        // / unstocked location) is now success:false but is NOT a hard error —
        // surface the actionable message as a warning, not a red error toast.
        sonnerToast.warning(`${prefix}${result.message}`, { duration: 8000 });
      } else if (result.shopifyErrors?.length) {
        sonnerToast.error(`${prefix}Push failed`, {
          description: result.shopifyErrors[0],
          duration: 8000,
        });
      } else {
        sonnerToast.error(`${prefix}${result.message}`);
      }
      setPushingLocationId(null);

      // Refetch breakdown to update push safety state (perLocationPushUnlocked derived from backend)
      void breakdownQuery.refetch();
    },
    onError: (error) => {
      sonnerToast.error(`Push failed: ${error.message}`);
      setPushingLocationId(null);
    },
  });

  const breakdownQuery = trpc.inventory.getProductLocationBreakdown.useQuery(
    { skuId },
    {
      enabled: open && Boolean(product?.id),
    }
  );

  // Phase 92 (D-13, UI-SPEC 1.6): a supply alias (a client product built on
  // the 3PL's blank) holds no stock of its own; the server refuses every
  // stock write on it. Read from the breakdown, not the list row, so it holds
  // from any location view. Stock controls render read-only with the R3
  // reason; threshold and hold-back stay editable.
  const supplyAlias = breakdownQuery.data?.supplyAlias ?? null;
  const aliasReadOnlyReason = supplyAlias
    ? aliasStockWriteMessage(t, {
        sku: breakdownQuery.data?.product?.sku || product?.sku || null,
        supplyAlias,
        isClientCaller: isOwnRow(product?.ownerOrganizationId, organization?.id),
      })
    : null;

  useEffect(() => {
    if (!breakdownQuery.data) return;

    setStockInputs(
      breakdownQuery.data.locations.reduce<Record<string, string>>((acc, row) => {
        acc[row.locationId] = String(row.onHand);
        return acc;
      }, {})
    );
    setThresholdInputs(
      breakdownQuery.data.locations.reduce<Record<string, string>>((acc, row) => {
        acc[row.locationId] = row.notify ? String(row.notify) : '';
        return acc;
      }, {})
    );
    setHoldBackInputs(
      breakdownQuery.data.locations.reduce<Record<string, string>>((acc, row) => {
        // `!= null`, NOT a falsy check: 0 is a real configured value here.
        acc[row.locationId] = row.pushSafetyBuffer != null ? String(row.pushSafetyBuffer) : '';
        return acc;
      }, {})
    );
  }, [breakdownQuery.data]);

  useEffect(() => {
    if (open) return;
    setPendingLocationId(null);
    setPushingLocationId(null);
    setStockInputs({});
    setThresholdInputs({});
    setInvalidFields({});
    setShowPushConfirm(false);
    setIsSavingAll(false);
    setScrapLocation(null);
  }, [open]);

  const detailProduct = breakdownQuery.data?.product;

  const summary = useMemo(() => {
    const title = detailProduct?.title?.trim() || product?.title?.trim() || product?.category.type || "-";
    // Keep these nullable (not defaulted to "-") so joinParts can drop absent
    // segments cleanly in the subtitle below. (BLU-296)
    const type = detailProduct?.category.type || product?.category.type || null;
    const color = detailProduct?.category.color || product?.category.color || null;
    const size = detailProduct?.category.size || product?.category.size || null;
    const sku = detailProduct?.sku || product?.sku || "-";

    // ace/trims-ux: `is_trim` now flows through from sku_inventory_with_details
    // (via getProducts -> useInventoryQueries -> Product.isTrim) — closes the
    // deferred badge site from the trims work. `detailProduct` (the
    // getProductLocationBreakdown response) doesn't carry is_trim, so the
    // override is sourced from `product` (the row already selected from the
    // list) rather than the breakdown query.
    const productionType = product?.isTrim
      ? "Trim"
      : detailProduct?.productionType || product?.productionType || "POD";

    return {
      sku,
      title,
      type,
      color,
      size,
      productionType,
      imageUrl: detailProduct?.imageUrl || product?.image_url || undefined,
    };
  }, [detailProduct, product]);

  const handleSaveRow = async (
    locationId: string,
    // Carries `pushSafetyBuffer` so the hold-back comparison reads a typed
    // field instead of casting the row inline.
    location: { onHand: number; notify?: number; pushSafetyBuffer?: number | null },
  ) => {
    if (!product) return;

    // Clear previous validation state for this row
    setInvalidFields((prev) => ({ ...prev, [locationId]: null }));

    // --- Stock validation ---
    const rawStock = stockInputs[locationId];
    const parsedStock = rawStock === '' ? null : Number(rawStock);

    if (parsedStock !== null && (!Number.isInteger(parsedStock) || parsedStock < 0)) {
      setInvalidFields((prev) => ({ ...prev, [locationId]: 'stock' }));
      toast({
        title: "Invalid stock level",
        description: "Stock level must be a whole number greater than or equal to 0.",
        variant: "destructive",
      });
      return;
    }

    const stockChanged =
      onSetStock && !supplyAlias && parsedStock !== null && parsedStock !== location.onHand;

    // --- Threshold validation ---
    const rawThreshold = thresholdInputs[locationId];
    const parsedThreshold = rawThreshold === '' ? 0 : Number(rawThreshold);

    if (!Number.isInteger(parsedThreshold) || parsedThreshold < 0) {
      setInvalidFields((prev) => ({ ...prev, [locationId]: 'threshold' }));
      toast({
        title: "Invalid threshold",
        description: "Alert threshold must be a whole number greater than or equal to 0.",
        variant: "destructive",
      });
      return;
    }

    const serverThreshold = location.notify ?? 0;
    const thresholdChanged = parsedThreshold !== serverThreshold;

    // --- Hold-back validation ---
    // Hoisted ABOVE the writes below. Validating it inside the try left the
    // stock and threshold mutations already committed when the hold-back turned
    // out to be invalid: a half-saved row plus a modal showing values the
    // server no longer agreed with.
    // `value: null` CLEARS the override (falls back to the type rule / org
    // buffer); 0 means "hold nothing back here". The wire field is nullable,
    // not optional, so an explicit null is sent rather than an omitted key
    // (which would read as "no change").
    const rawHoldBack = holdBackInputs[locationId];
    const holdBackTouched = rawHoldBack !== undefined;
    const parsedHoldBack =
      !holdBackTouched || rawHoldBack.trim() === '' ? null : Number(rawHoldBack);

    if (
      holdBackTouched &&
      parsedHoldBack !== null &&
      // Upper bound matches the Zod schema on setPushSafetyBuffer, so an
      // over-large entry reads as a field error rather than a raw 400.
      (!Number.isInteger(parsedHoldBack) || parsedHoldBack < 0 || parsedHoldBack > 100000)
    ) {
      setInvalidFields((prev) => ({ ...prev, [locationId]: 'holdBack' }));
      toast({
        title: 'Invalid hold-back',
        description: 'Hold-back must be a whole number of units, 0 to 100,000. Leave it blank to fall back.',
        variant: 'destructive',
      });
      return;
    }

    const serverHoldBack = location.pushSafetyBuffer ?? null;
    const holdBackChanged = holdBackTouched && parsedHoldBack !== serverHoldBack;

    // A hold-back-only edit is a real edit. Without it in this guard, Save
    // enabled itself (dirtyLocationIds counts hold-back) and then silently
    // dropped the change, or scolded the operator about a stock box they never
    // touched.
    if (!stockChanged && !thresholdChanged && !holdBackChanged) {
      if (rawStock === '' && onSetStock) {
        setInvalidFields((prev) => ({ ...prev, [locationId]: 'stock' }));
        toast({
          title: "Stock level required",
          description: "Enter a stock level or leave it unchanged.",
          variant: "destructive",
        });
      }
      return;
    }

    setPendingLocationId(locationId);
    try {
      const promises: Promise<unknown>[] = [];

      if (stockChanged && onSetStock && parsedStock !== null) {
        // BLU-3xx Phase 47.01.2-B-fix3.1: pass the Product directly. The
        // detail modal's `product` prop is the same Product the caller
        // already has — no lookup needed.
        // BLU-681: pass the row's owner org explicitly (a SKU's inventory
        // rows all belong to the SKU's org, so this is valid per-location).
        promises.push(
          Promise.resolve(
            onSetStock(product, parsedStock, locationId, product.ownerOrganizationId),
          ),
        );
      }
      if (thresholdChanged) {
        promises.push(updateThresholdMutation.mutateAsync({
          skuId: product.id,
          locationId,
          notify: parsedThreshold,
        }));
      }

      // Stock Hold-Back layer 1 (validated above).
      if (holdBackChanged) {
        promises.push(
          setHoldBackMutation.mutateAsync({
            skuIds: [product.id],
            locationId,
            // BLU-681, same as the stock write above: name the row's owner org
            // explicitly. Without it an owner editing a client's SKU at a
            // shared location trips the backend's ownership pre-check ("One or
            // more SKUs do not belong to the target organization"), because the
            // check falls back to the CALLER's org.
            targetOrganizationId: product.ownerOrganizationId,
            value: parsedHoldBack,
          })
        );
      }

      await Promise.all(promises);
      // Only show toast for saves the row itself doesn't visibly confirm (the
      // stock mutation shows its own toast via useInventoryMutations).
      if (thresholdChanged && !stockChanged) {
        sonnerToast.success("Alert threshold updated");
      }
      if (holdBackChanged && !stockChanged && !thresholdChanged) {
        sonnerToast.success("Hold-back updated", {
          description: "Applies on the next push cycle.",
        });
      }
    } catch {
      // Every mutation on this path raises its own destructive toast. Swallowing
      // here stops one failed row from aborting the rest of a Save All run (and
      // from surfacing as an unhandled rejection).
    } finally {
      // Re-read on BOTH paths: after a partial failure the modal must show what
      // the server actually has, not the inputs the operator typed.
      void breakdownQuery.refetch();
      void utils.inventory.getProducts.invalidate();
      void utils.inventory.getProductLocationBreakdown.invalidate({ skuId: product.id });
      setPendingLocationId(null);
    }
  };

  const handlePushToShopify = (locationId?: string) => {
    if (!product?.id || !organization?.id || productPushContext.blocked) return;
    setPushingLocationId(locationId ?? "all");
    pushSku.mutate({ expectedOrganizationId: organization.id, skuId: product.id, locationId });
  };

  const handleHeaderPush = () => {
    const locations = breakdownQuery.data?.locations ?? [];
    const zeroLocations = locations.filter(l => l.available <= 0);
    if (zeroLocations.length > 0) {
      setShowPushConfirm(true);
    } else {
      handlePushToShopify();
    }
  };

  const handleConfirmHeaderPush = () => {
    handlePushToShopify();
    setShowPushConfirm(false);
  };

  const zeroStockItems = (breakdownQuery.data?.locations ?? [])
    .filter(l => l.available <= 0)
    .map(l => ({
      sku: summary.sku,
      locationName: l.locationName,
      previousQty: l.available,
      newQty: 0,
    }));

  const isPushing = pushSku.isPending;
  // BLU-343: Push safety derived from backend data — not session-only state.
  // Unlocked when all locations have been pushed before, or auto-push is active.
  const perLocationPushUnlocked = breakdownQuery.data?.perLocationPushUnlocked ?? false;
  const isSkuExcluded = breakdownQuery.data?.isSkuExcluded ?? false;
  const [isSavingAll, setIsSavingAll] = useState(false);

  // BLU-343: Track which locations have unsaved changes
  const dirtyLocationIds = useMemo(() => {
    const dirty = new Set<string>();
    for (const loc of breakdownQuery.data?.locations ?? []) {
      const stockVal = stockInputs[loc.locationId];
      const threshVal = thresholdInputs[loc.locationId];
      if (stockVal !== undefined && Number(stockVal) !== loc.onHand) dirty.add(loc.locationId);
      if (threshVal !== undefined && Number(threshVal) !== (loc.notify ?? 0)) dirty.add(loc.locationId);
      // Nullable compare. A `Number('') !== (x ?? 0)` shape here would make
      // clearing an override never register as a change, so Save would stay
      // disabled and the override could not be removed.
      const holdVal = holdBackInputs[loc.locationId];
      if (holdVal !== undefined) {
        const parsed = holdVal.trim() === '' ? null : Number(holdVal);
        if (parsed !== (loc.pushSafetyBuffer ?? null)) dirty.add(loc.locationId);
      }
    }
    return dirty;
  }, [stockInputs, thresholdInputs, holdBackInputs, breakdownQuery.data]);

  // BLU-343: Save All — saves all dirty locations at once
  const handleSaveAll = useCallback(async () => {
    if (!product || dirtyLocationIds.size === 0) return;
    setIsSavingAll(true);
    try {
      const locations = breakdownQuery.data?.locations ?? [];
      for (const loc of locations) {
        if (dirtyLocationIds.has(loc.locationId)) {
          await handleSaveRow(loc.locationId, loc);
        }
      }
      // No extra toast here — each handleSaveRow triggers its own toast
      // via the onSetStock callback (useInventoryMutations)
    } finally {
      setIsSavingAll(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, dirtyLocationIds, breakdownQuery.data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Product Inventory Details</DialogTitle>
          <DialogDescription>
            Review and set stock levels by location for this SKU.
          </DialogDescription>
        </DialogHeader>

        {!product ? null : (
          <div className="space-y-4 min-w-0">
            <div className="rounded-md border p-3">
              <div className="flex items-center gap-4 flex-wrap">
                <ProductImageWithHover imageUrl={summary.imageUrl} productSku={summary.sku} size="large" />
                <div className="flex-1 space-y-1">
                  <p className="font-mono text-sm">{summary.sku}</p>
                  <p className="text-sm font-medium">{summary.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {joinParts([summary.type, summary.color, summary.size, summary.productionType])}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => void handleSaveAll()}
                    disabled={isSavingAll || dirtyLocationIds.size === 0}
                  >
                    {isSavingAll
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save Changes{dirtyLocationIds.size > 0 ? ` (${dirtyLocationIds.size})` : ''}
                  </Button>
                  {isAdmin() && !productPushContext.blocked && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleHeaderPush}
                      disabled={isPushing}
                    >
                      {isPushing && pushingLocationId === "all"
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        : <ArrowUpFromLine className="h-3.5 w-3.5 mr-1.5" />}
                      Push All Locations
                    </Button>
                  )}
                  {isAdmin() && productPushContext.blocked && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex h-8 items-center gap-1 px-2 text-sm text-muted-foreground/50"
                            tabIndex={0}
                            role="status"
                            aria-label={t('push.crossOrg.modalWarning')}
                          >
                            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('push.crossOrg.switchClient')}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-80">
                          <p className="text-xs">{t('push.crossOrg.modalWarning')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </div>

            {productPushContext.blocked && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
                {t('push.crossOrg.modalWarning')}
              </p>
            )}

            {breakdownQuery.isLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading location breakdown...
              </div>
            ) : breakdownQuery.error ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {breakdownQuery.error.message}
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-auto rounded-md border">
                <Table className="[&_th]:px-2 [&_td]:px-2">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Location</TableHead>
                      <TableHead className="text-center whitespace-nowrap">On Hand</TableHead>
                      <TableHead className="text-center">Available</TableHead>
                      <TableHead className="text-center">Committed</TableHead>
                      <TableHead className="text-center">Unavailable</TableHead>
                      <TableHead className="text-center">Incoming</TableHead>
                      <TableHead className="w-20 text-center whitespace-nowrap">Alert At</TableHead>
                      {/* Wraps to two lines on purpose: nowrap here would widen
                          the whole breakdown table and hand the modal a
                          horizontal scrollbar. */}
                      <TableHead className="w-28 text-center">Hold-back (off Shopify)</TableHead>
                      <TableHead className="w-28 text-center whitespace-nowrap">Set Stock</TableHead>
                      <TableHead className="w-28 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {breakdownQuery.data?.locations.length ? (
                      breakdownQuery.data.locations.map((location) => {
                        const isSaving = pendingLocationId === location.locationId;
                        // Per-FIELD, not per-row: `dirtyLocationIds` also counts
                        // threshold and hold-back edits, so a hold-back change
                        // painted the unsaved stripe on the Set Stock box the
                        // operator never touched.
                        const stockDirty =
                          stockInputs[location.locationId] !== undefined &&
                          Number(stockInputs[location.locationId]) !== location.onHand;
                        const isLocationPushing = isPushing && (pushingLocationId === location.locationId || pushingLocationId === "all");
                        // BLU-1268: a just-in-time pair has no count (its figures are
                        // the store constant), so it shows none, takes no stock edit
                        // and no scrap; the Supply badge by the name says why.
                        // BLU-1526: that holds when its supplier is out of it too (the
                        // edit stays refused); the chip by the name says it is out.
                        const justInTime = location.stockMode === 'just_in_time';
                        const notCounted = (
                          <span className="text-muted-foreground">
                            <span aria-hidden="true">-</span>
                            <span className="sr-only">{t('supply.notCounted')}</span>
                          </span>
                        );
                        return (
                          <TableRow key={location.locationId}>
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span>{location.locationName}</span>
                                {location.stockMode && location.stockMode !== 'stocked' && (
                                  <SupplyBadge mode={location.stockMode} srPrefix={t('baseGarments.supply.label')} />
                                )}
                                {location.supplierOut === true && (
                                  <SupplierOutChip discontinued={location.supplierDiscontinued === true} />
                                )}
                                {(location.isExcludedFromPush || isSkuExcluded) && (
                                  <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                    Excluded
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center tabular-nums">{justInTime ? notCounted : location.onHand}</TableCell>
                            <TableCell className="text-center tabular-nums">
                              {justInTime ? notCounted : (
                              <span className="inline-flex items-center justify-center gap-1">
                                <span>{location.available}</span>
                                {isHybridEligibleProduct && location.hybridMode && (
                                  <AdditiveBreakdownIcon
                                    ownQty={location.available}
                                    relatedQty={location.hybridRelatedQty ?? 0}
                                    relatedSku={location.hybridRelatedSku ?? null}
                                  />
                                )}
                              </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center tabular-nums">{justInTime ? notCounted : location.committed}</TableCell>
                            <TableCell className="text-center tabular-nums">{justInTime ? notCounted : location.unavailable}</TableCell>
                            <TableCell className="text-center tabular-nums">{justInTime ? notCounted : location.incoming}</TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                value={thresholdInputs[location.locationId] ?? ''}
                                onChange={(event) => {
                                  setInvalidFields((prev) => ({ ...prev, [location.locationId]: null }));
                                  setThresholdInputs((prev) => ({
                                    ...prev,
                                    [location.locationId]: event.target.value,
                                  }));
                                }}
                                placeholder="0"
                                className={`mx-auto w-20 text-center ${invalidFields[location.locationId] === 'threshold' ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                disabled={isSaving || managedByProviderLocationIds.has(location.locationId)}
                                title={
                                  managedByProviderLocationIds.has(location.locationId)
                                    ? 'Stock adjustments at this location are managed by your 3PL provider.'
                                    : undefined
                                }
                              />
                            </TableCell>
                            {/* Stock Hold-Back layer 1 — the per-SKU exception.
                                Blank falls back to the product-type rule; 0 is an
                                explicit "hold nothing back at this location". The
                                meta line below is what makes those two states
                                distinguishable at a glance.
                                NOT gated on managedByProviderLocationIds: that
                                predicate is the STOCK-write gate, and the backend
                                guard for this field
                                (assertCallerManagesLocationPushConfig)
                                deliberately allows an active junction client to
                                configure its own push regardless of it. The
                                server is the authz boundary; a refusal arrives as
                                the mutation's destructive toast. */}
                            <TableCell className="text-center">
                              <Input
                                // TEXT, not number: `type="number"` reports ''
                                // for an unparseable entry ("5e") while still
                                // showing it, so the meta line below would read
                                // "Fallback" and Save would CLEAR the override.
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="off"
                                value={holdBackInputs[location.locationId] ?? ''}
                                onChange={(event) => {
                                  setInvalidFields((prev) => ({ ...prev, [location.locationId]: null }));
                                  setHoldBackInputs((prev) => ({
                                    ...prev,
                                    [location.locationId]: event.target.value,
                                  }));
                                }}
                                placeholder="-"
                                aria-label={`Hold-back units at ${location.locationName}`}
                                className={`mx-auto w-20 text-center tabular-nums ${invalidFields[location.locationId] === 'holdBack' ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                disabled={isSaving}
                              />
                              <div className="mt-0.5 min-h-4 text-[10px] text-muted-foreground">
                                {(holdBackInputs[location.locationId] ?? '').trim() === ''
                                  ? 'Fallback'
                                  : Number(holdBackInputs[location.locationId]) === 0
                                    ? 'None'
                                    : ''}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {justInTime ? (
                                <span className="text-xs text-muted-foreground">{t('supply.notCounted')}</span>
                              ) : aliasReadOnlyReason ? (
                                // Read-only at the trigger: no input to type into,
                                // the reason sits under the table.
                                <span
                                  className="inline-flex items-center gap-1 tabular-nums text-muted-foreground"
                                  title={aliasReadOnlyReason}
                                >
                                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                                  {location.onHand}
                                </span>
                              ) : (
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                value={stockInputs[location.locationId] ?? String(location.onHand)}
                                onChange={(event) => {
                                  setInvalidFields((prev) => ({ ...prev, [location.locationId]: null }));
                                  setStockInputs((prev) => ({
                                    ...prev,
                                    [location.locationId]: event.target.value,
                                  }));
                                }}
                                className={`mx-auto w-24 text-center ${invalidFields[location.locationId] === 'stock' ? 'border-red-500 ring-1 ring-red-500' : stockDirty ? 'border-l-2 border-l-threadheads-purple' : ''}`}
                                disabled={isSaving || managedByProviderLocationIds.has(location.locationId)}
                                title={
                                  managedByProviderLocationIds.has(location.locationId)
                                    ? 'Stock adjustments at this location are managed by your 3PL provider.'
                                    : undefined
                                }
                              />
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center gap-1">
                                {isAdmin() && productPushContext.blocked ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="inline-flex h-8 items-center gap-1 px-2 text-sm text-muted-foreground/40"
                                          tabIndex={0}
                                          role="status"
                                          aria-disabled="true"
                                          aria-label={t('push.crossOrg.modalWarning')}
                                        >
                                          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                                          <span>Push</span>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-80">
                                        <p className="text-xs">{t('push.crossOrg.modalWarning')}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : isAdmin() && perLocationPushUnlocked ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-muted-foreground hover:text-primary"
                                    title={`Push ${location.locationName}: sets quantity on Shopify for this location only`}
                                    aria-label={`Push ${location.locationName} to Shopify`}
                                    onClick={() => handlePushToShopify(location.locationId)}
                                    disabled={isLocationPushing}
                                  >
                                    {isLocationPushing
                                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                      : <ArrowUpFromLine className="h-3.5 w-3.5 mr-1" />}
                                    Push
                                  </Button>
                                ) : isAdmin() ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="inline-flex items-center gap-1 h-8 px-2 text-muted-foreground/40 cursor-not-allowed text-sm"
                                          tabIndex={0}
                                          role="status"
                                          aria-disabled="true"
                                          aria-label={`Push locked for ${location.locationName}. Push to all locations first to ensure accurate stock.`}
                                        >
                                          <Lock className="h-3.5 w-3.5" />
                                          <span>Push</span>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="text-xs">Push to all locations first to ensure accurate stock</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : null}
                                {/* BLU-681: scrap is a real stock decrement —
                                    hidden on provider-managed rows (the note
                                    below carries the reason; the backend also
                                    FORBIDs it). */}
                                {canRecordScrap && !justInTime && !aliasReadOnlyReason && !managedByProviderLocationIds.has(location.locationId) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                    title={`Record scrap for ${location.locationName}`}
                                    aria-label={`Record scrap for ${location.locationName}`}
                                    onClick={() =>
                                      setScrapLocation({
                                        locationId: location.locationId,
                                        locationName: location.locationName,
                                        onHand: location.onHand,
                                      })
                                    }
                                  >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                                    Scrap
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} className="h-24 text-center">
                          No inventory rows found for this SKU.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* BLU-681: one-line reason when any breakdown row is read-only
                because this org is a CLIENT at that shared 3PL location. */}
            {breakdownQuery.data?.locations.some((row) =>
              managedByProviderLocationIds.has(row.locationId),
            ) && (
              <p className="text-xs text-muted-foreground">
                {/* "some": with BLU-711 a client can be opted in at one shared
                    location and not another, so the blanket claim would be
                    false next to an editable shared row. */}
                Stock adjustments at some shared locations are managed by your 3PL provider.
              </p>
            )}

            {/* BLU-1268: why a just-in-time location shows no stock. BLU-1526: the
                store does not always show it in stock once its supplier is out,
                so a note that says it does is only for the locations that are not. */}
            {breakdownQuery.data?.locations.some((row) => row.stockMode === 'just_in_time' && row.supplierOut !== true) && (
              <p className="text-xs leading-normal text-muted-foreground">{t('supply.detailNote')}</p>
            )}
            {breakdownQuery.data?.locations.some((row) => row.supplierOut === true) && (
              <p className="text-xs leading-normal text-muted-foreground">
                {breakdownQuery.data.locations.some((row) => row.supplierDiscontinued === true)
                  ? t('supply.detailNoteDiscontinued')
                  : t('supply.detailNoteOut')}
              </p>
            )}

            {/* Phase 92: why an alias's stock cannot be changed here. */}
            {aliasReadOnlyReason && (
              <p className="text-xs leading-normal text-muted-foreground" role="status">
                {aliasReadOnlyReason}
              </p>
            )}

            {breakdownQuery.isFetching && !breakdownQuery.isLoading && (
              <div className="flex items-center justify-end text-xs text-muted-foreground">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Refreshing...
              </div>
            )}
          </div>
        )}

        <PushConfirmDialog
          open={showPushConfirm}
          onOpenChange={setShowPushConfirm}
          onConfirm={handleConfirmHeaderPush}
          goingToZero={zeroStockItems}
          totalChanges={breakdownQuery.data?.locations.length ?? 0}
          isPending={isPushing && pushingLocationId === 'all'}
          mode="single"
          hybridQtyEnabled={
            isHybridEligibleProduct &&
            (breakdownQuery.data?.locations.some((l) => l.hybridMode) ?? false)
          }
        />

        {scrapLocation && (
          <RecordScrapModal
            isOpen={scrapLocation !== null}
            onClose={() => setScrapLocation(null)}
            sku={summary.sku}
            skuId={skuId}
            location={scrapLocation}
            onRecorded={() => {
              void breakdownQuery.refetch();
              void utils.inventory.getProducts.invalidate();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
